import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import type { AuthenticationFlowPageProps } from '../AuthenticationFlow';
import { AuthenticationEmailInput } from './AuthenticationEmailInput';
import { AuthenticationPasswordInput } from './AuthenticationPasswordInput';
import { useAuthSubmit } from 'src/hooks/useAuthSubmit';
import { useAuth } from 'src/contexts/AuthContext';
import { getUsername } from 'src/util/getUsername';

export type AuthenticationEmailSignUpPageProps = AuthenticationFlowPageProps;

export function AuthenticationEmailSignUpPage({
  pageId: pageID,
  onPageComplete,
}: AuthenticationEmailSignUpPageProps) {
  const { userCredentials, setUserCredentials, attemptSubmit } =
    useAuthSubmit();
  const { handleCreateAccount } = useAuth();
  if (!pageID || !onPageComplete) return null;
  const onSubmit = async () => {
    const result = await handleCreateAccount(
      userCredentials.email,
      userCredentials.password,
    );
    if (result) {
      const { sendAuthEmail } = await import(
        '../../../firebaseCloud/mail/sendAuthEmail'
      );
      await sendAuthEmail({
        to: userCredentials.email,
        mode: 'verifyEmail',
        username: await getUsername(userCredentials.email),
      });
      onPageComplete(pageID);
    } else {
      console.error('Error creating account');
    }
  };

  return (
    <Stack spacing={'32px'} my={'64px'} width={'100%'} maxWidth={'420px'}>
      <Stack spacing={'8px'}>
        <Typography
          variant="h4"
          fontWeight="500"
          textTransform={'capitalize'}
          textAlign={'center'}
        >
          Sign Up
        </Typography>
        <Typography variant="body1" textAlign={'center'}>
          Email not found. Create a BluMint account now?
        </Typography>
      </Stack>
      <AuthenticationEmailInput />
      <AuthenticationPasswordInput
        setUserCredentials={setUserCredentials}
        onSubmit={() => {
          return attemptSubmit({
            onSubmit,
            userCredentialsUpdated: userCredentials,
          });
        }}
      />
      <Box textAlign={'center'}>
        <LoadingButton
          variant="contained"
          size="large"
          fullWidth
          onClick={async () => {
            return await attemptSubmit({
              onSubmit,
              userCredentialsUpdated: userCredentials,
            });
          }}
        >
          Sign Up
        </LoadingButton>
      </Box>
    </Stack>
  );
}
